import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { Section } from "@/components/Section";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const categories = ["All", ...Array.from(new Set(items.map((i) => i.category)))];
  const filtered = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <>
      <section className="bg-[#1A1A1A] text-white py-20 noise-overlay">
        <Section>
          <div className="label-eyebrow mb-4">Gallery</div>
          <h1 className="heading-xl text-4xl sm:text-5xl lg:text-6xl">FROM THE <span className="text-[#E65C00]">FIELD.</span></h1>
          <p className="mt-4 text-gray-400 max-w-xl">Machinery, projects and people. The everyday view from Tara Autohub job sites.</p>
        </Section>
      </section>

      <Section className="py-12">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="gallery-filters">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`px-4 py-2 text-xs uppercase font-bold tracking-widest border-2 transition-colors ${
                filter === c ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-gray-300 text-gray-600 hover:border-[#1A1A1A]"
              }`}
              data-testid={`gallery-cat-${c}`}
            >
              {c}
            </button>
          ))}
        </div>

        <motion.div layout className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <AnimatePresence mode="popLayout">
            {filtered.map((g, i) => (
              <motion.button
                key={g.id}
                layout
                initial={{ opacity: 0, y: 28, scale: 0.92 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.45, delay: Math.min(i * 0.04, 0.4), ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -4 }}
                onClick={() => setLightbox(g)}
                className="group relative aspect-square overflow-hidden bg-gray-100 shadow-sm"
                data-testid={`gallery-item-${g.id}`}
              >
                <motion.img
                  src={mediaUrl(g.image_url)}
                  alt={g.title}
                  className="w-full h-full object-cover"
                  initial={{ scale: 1.08 }}
                  animate={{ scale: 1 }}
                  whileHover={{ scale: 1.12 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/85 via-[#1A1A1A]/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 text-left">
                  <div className="text-[10px] uppercase tracking-widest text-[#FFCC00] font-bold">{g.category}</div>
                  <div className="text-white font-heading font-black mt-1 text-sm leading-tight">{g.title}</div>
                </div>
                <div className="absolute top-3 right-3 w-7 h-7 bg-[#E65C00]/0 group-hover:bg-[#E65C00] flex items-center justify-center transition-all duration-300 scale-75 group-hover:scale-100">
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>

        {filtered.length === 0 && <div className="text-center py-20 border border-dashed border-gray-300 text-gray-500">No images in this category yet.</div>}
      </Section>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox"
          >
            <button className="absolute top-4 right-4 text-white" onClick={() => setLightbox(null)} aria-label="Close lightbox"><X className="w-7 h-7" /></button>
            <motion.div
              className="max-w-4xl w-full"
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <img src={mediaUrl(lightbox.image_url)} alt={lightbox.title} className="w-full max-h-[80vh] object-contain" />
              <div className="text-white text-center mt-4">
                <div className="text-xs uppercase tracking-widest text-[#FFCC00]">{lightbox.category}</div>
                <div className="font-heading font-black text-xl mt-1">{lightbox.title}</div>
                {lightbox.description && <div className="text-gray-400 text-sm mt-2">{lightbox.description}</div>}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
